import React, { Fragment, useContext } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ProfileMenu } from '@td-client-frontend/common';
import { NavDropdown, NavDropdownItem, NavItem } from '../NavBar';
import { PermMode } from '../WithPermission';
import { PendingChangesContext } from '../../context/PendingChangesContext';
import BasicNav from './BasicNav';
import { Permissions } from '../../@types/Permissions';

interface LayoutProps {
  children?: React.ReactNode;
}

interface FooterLink {
  text: string;
  to: string;
}

const GROUP_PERMS = [Permissions.GroupRead, Permissions.GroupWrite];
const AUDIT_PERMS = [Permissions.AuditRead, Permissions.AuditWrite, Permissions.AuditCreate];
const RESELLER_PERMS = [Permissions.ResellerRead, Permissions.ResellerWrite, Permissions.ResellerCreate];
const MARKETING_PROFILE_READ_PERMS = [
  Permissions.MarketingProfileRetentionRead,
  Permissions.MarketingProfileRegistrationRead,
  Permissions.MarketingProfilePreferencesRead
];
const ROLE_PERMS = [Permissions.RoleRead, Permissions.RoleWrite];
const USER_PERMS = [Permissions.UserRead, Permissions.UserWrite];

const links: FooterLink[] = [
  { text: 'Cookie Policy', to: 'https://teladochealth.com/cookie-policy/' },
  { text: 'Web and Mobile Privacy Policy', to: 'https://teladochealth.com/privacy-policy/' },
  { text: 'Web and Mobile Terms and Conditions', to: 'https://teladochealth.com/terms-and-conditions/' }
];

const divider = ' | ';

export const getCurrentYear = (): number => {
  return new Date().getFullYear();
};

export function MainContent({ children }: { children: React.ReactNode }) {
  return (
    <main id="content" className="flex flex-col flex-1 min-h-auto">
      {children}
    </main>
  );
}

const AuthLayout = ({ children }: React.PropsWithChildren<LayoutProps>) => {
  const { authState, logout } = useAuth();
  const { checkAndPrompt } = useContext(PendingChangesContext);

  if (!authState.isAuthenticated || authState.userInfo?.shouldUpdatePassword) {
    return (
      <div className="flex flex-col h-screen min-h-0 bg-grey">
        <main id="content" className="flex flex-col flex-1">
          {children}
        </main>
        <footer className="p-3 text-center font-effra">
          Teladoc Health © {getCurrentYear()} Created by Teladoc Health
        </footer>
      </div>
    );
  }

  const checkAndLogout = (e: any) => {
    // to prevent redirecting from Link component
    e.preventDefault();
    checkAndPrompt(logout);
  };

  return (
    <div className="flex flex-col min-h-screen bg-grey" data-testid="auth-layout">
      <div className="sticky top-0 z-40 w-full bg-white">
        <BasicNav
          menu={
            <ProfileMenu
              className="w-full"
              dropdownItems={[
                { name: 'Settings', path: '/change-password' },
                { name: 'Logout', onClick: checkAndLogout, path: '' }
              ]}
              // @ts-ignore
              userInfo={authState.userInfo}
            />
          }
        >
          <div className="flex md:flex-row items-center h-16 space-x-1">
            <NavDropdown
              label="Groups"
              paths={['/groups', '/activity-log', '/reseller/jobs', '/reseller/templates']}
              requiredPerms={[...GROUP_PERMS, ...AUDIT_PERMS, ...RESELLER_PERMS]}
              mode={PermMode.ANY}
            >
              <NavDropdownItem name="groups" path="/groups" requiredPerms={GROUP_PERMS} mode={PermMode.ANY}>
                Employer groups
              </NavDropdownItem>
              <NavDropdownItem name="activity-log" path="/activity-log" requiredPerms={AUDIT_PERMS} mode={PermMode.ANY}>
                Groups activity log
              </NavDropdownItem>
              <NavDropdownItem
                name="reseller-jobs"
                path="/reseller/jobs"
                requiredPerms={RESELLER_PERMS}
                mode={PermMode.ANY}
              >
                Jobs
              </NavDropdownItem>
              <NavDropdownItem
                name="reseller-templates"
                path="/reseller/templates"
                requiredPerms={RESELLER_PERMS}
                mode={PermMode.ANY}
              >
                Template
              </NavDropdownItem>
            </NavDropdown>
            <NavItem
              name="members"
              path="/members"
              requiredPerms={[Permissions.MemberRead, Permissions.MemberWrite]}
              mode={PermMode.ANY}
            >
              Members
            </NavItem>
            <NavItem
              name="Reports"
              path="/reports"
              requiredPerms={[Permissions.ReportEligibilityRead]}
              mode={PermMode.ANY}
            >
              Reports
            </NavItem>
            <NavDropdown
              label="Marketing"
              paths={['/engagement-center', '/marketing-preferences', '/marketing-profiles']}
              requiredPerms={[
                Permissions.MarketingProfileListRead,
                Permissions.EngagementCenterRead,
                ...MARKETING_PROFILE_READ_PERMS
              ]}
              mode={PermMode.ANY}
            >
              <NavDropdownItem
                name="engagement-center"
                path="/engagement-center"
                requiredPerms={[Permissions.EngagementCenterRead]}
                mode={PermMode.ANY}
              >
                Engagement center
              </NavDropdownItem>

              <NavDropdownItem
                name="marketing-preferences"
                path="/marketing-preferences"
                requiredPerms={MARKETING_PROFILE_READ_PERMS}
                mode={PermMode.ANY}
              >
                Marketing preferences
              </NavDropdownItem>
              <NavDropdownItem
                name="marketing-profiles"
                path="/marketing-profiles"
                requiredPerms={[Permissions.MarketingProfileListRead]}
                mode={PermMode.ANY}
              >
                Marketing profiles
              </NavDropdownItem>
            </NavDropdown>
            <NavDropdown
              label="Admin"
              paths={['/roles', '/users', '/system-activity-log']}
              requiredPerms={[...ROLE_PERMS, ...USER_PERMS, Permissions.SystemActivityLogRead]}
              mode={PermMode.ANY}
            >
              <NavDropdownItem name="users" path="/users" requiredPerms={USER_PERMS} mode={PermMode.ANY}>
                Users
              </NavDropdownItem>
              <NavDropdownItem name="roles" path="/roles" requiredPerms={ROLE_PERMS} mode={PermMode.ANY}>
                Roles
              </NavDropdownItem>
              <NavDropdownItem
                name="system-activity-log"
                path="/system-activity-log"
                requiredPerms={[Permissions.SystemActivityLogRead]}
                mode={PermMode.ALL}
              >
                System activity log
              </NavDropdownItem>
            </NavDropdown>
          </div>
        </BasicNav>
      </div>
      <MainContent>{children}</MainContent>
      <footer className="sticky bottom-0 z-30 flex justify-between w-full px-6 py-4 mx-auto bg-white font-effra">
        <p className="font-extralight text-black-default">
          &copy; 2002 - {getCurrentYear()} Teladoc Health, Inc. All rights reserved
        </p>
        <p>
          {links.map(({ text, to }, index) => (
            <Fragment key={to}>
              <span className="mx-1">{index !== 0 && divider}</span>
              <a href={to} target="_blank" rel="noopener noreferrer" className="text-primary-500">
                {text}
              </a>
            </Fragment>
          ))}
        </p>
      </footer>
    </div>
  );
};

export default AuthLayout;
